/**
 * Front matter, parsed without a dependency.
 *
 * Content pages are Markdown with a small fenced header:
 *
 *   ---
 *   slug: arrive
 *   shell: guest
 *   section: arrive
 *   order: 10
 *   title_es: La llegada
 *   title_en: Getting here
 *   tiers: all_inclusive, standard
 *   offline: true
 *   ---
 *
 * The grammar is deliberately tiny: one `key: value` per line, no nesting, no
 * anchors, no multi line strings. A comma separated value becomes an array.
 * That is all a guidebook page has ever needed, and it means a writer can edit
 * a page without learning YAML.
 */

export type FrontMatterValue = string | number | boolean | string[];
export type RawFrontMatter = Record<string, FrontMatterValue>;

export type ParsedDocument = {
  data: RawFrontMatter;
  body: string;
};

const FENCE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/** These keys keep their commas instead of becoming arrays. */
const NEVER_SPLIT = new Set(['title_es', 'title_en', 'lede_es', 'lede_en', 'slug']);

export function parseFrontMatter(source: string): ParsedDocument {
  const match = FENCE.exec(source);
  if (!match) return { data: {}, body: source.trim() };

  const data: RawFrontMatter = {};
  for (const line of (match[1] ?? '').split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const colon = trimmed.indexOf(':');
    if (colon === -1) continue;

    const key = trimmed.slice(0, colon).trim();
    const raw = trimmed.slice(colon + 1).trim();
    data[key] = coerce(key, raw);
  }

  return { data, body: source.slice(match[0].length).trim() };
}

function coerce(key: string, raw: string): FrontMatterValue {
  const unquoted = raw.replace(/^["'](.*)["']$/, '$1');

  if (unquoted === 'true') return true;
  if (unquoted === 'false') return false;
  if (unquoted !== '' && !Number.isNaN(Number(unquoted)) && /^-?\d+(\.\d+)?$/.test(unquoted)) {
    return Number(unquoted);
  }
  if (!NEVER_SPLIT.has(key) && unquoted.includes(',')) {
    return unquoted
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return unquoted;
}
